import { Add } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { BASE_URL } from "lib/utilities/globalConstants";
import { UserAuth } from "provider/AuthProvider";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

const UpdateCandidateNotes = ({ label, viewId, id, notes = "" }) => {
  const [updating, setUpdating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    notes: notes || "",
  });

  const { user } = UserAuth();
  const queryClient = useQueryClient();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.notes.trim()) {
      errors.notes = "Notes are required";
    }
    return errors;
  };

  const handleUpdate = async (clearNotes = false) => {
    if (!clearNotes) {
      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }

    try {
      clearNotes ? setClearing(true) : setUpdating(true);
      const response = await fetch(`${BASE_URL}admin/updateCandidateNote/${viewId}/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          notes: clearNotes ? "" : formData.notes,
        }),
      });

      if (response.ok) {
        setOpenModal(false);
        queryClient.invalidateQueries(["getViewCandidates"]);
        toast.success("Notes updated successfully");
      } else {
        const errorData = await response.json();
        toast.error(errorData.message);
      }
    } catch (error) {
      console.error("Error updating notes:", error.message);
      toast.error("An unexpected error occurred");
    } finally {
      clearNotes ? setClearing(false) : setUpdating(false);
    }
  };

  // Reset form data when modal opens
  useEffect(() => {
    if (openModal) {
      setFormData({
        notes: notes || "",
      });
    }
  }, [openModal, notes]);

  const handleCloseModal = () => {
    if (!updating) {
      setOpenModal(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpenModal(true)}
        className="flex items-center justify-center px-4 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 border border-red-200 disabled:cursor-not-allowed disabled:opacity-70"
      >
        <span className="mr-2">{label}</span>
        <Add style={{ height: "16px", width: "16px" }} />
      </button>
      <Dialog maxWidth="md" open={openModal} fullWidth onClose={handleCloseModal}>
        <div className="overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b py-2 px-5 sticky top-0 bg-white z-50">
            <Typography variant="h5" className="font-semibold text-gray-900">
              Notes
            </Typography>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                disabled={updating}
                onClick={handleCloseModal}
                className="text-blue-700 text-sm font-medium bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors px-4 py-1.5 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleUpdate(true)}
                disabled={clearing}
                className="text-white text-sm font-medium bg-red-600 hover:bg-red-700 transition-colors px-4 py-1.5 rounded-lg flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {clearing ? <CircularProgress size={16} color="inherit" className="mr-2" /> : null}
                Clear Notes
              </button>
              <button
                type="button"
                onClick={() => handleUpdate(false)}
                disabled={updating}
                className="text-white text-sm font-medium bg-blue-600 hover:bg-blue-700 transition-colors px-4 py-1.5 rounded-lg flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {updating ? <CircularProgress size={16} color="inherit" className="mr-2" /> : null}
                Update Notes
              </button>
            </div>
          </div>
          <div className="flex flex-col overflow-auto p-5 space-y-3">
            <div className="space-y-3 w-full">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700">Notes</p>
              </div>
              <FormControl error={!!formErrors.notes} disabled={updating} fullWidth>
                <TextField
                  placeholder="Enter notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  error={!!formErrors.notes}
                  disabled={updating}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#fff",
                    },
                  }}
                />
                <FormHelperText>{formErrors.notes}</FormHelperText>
              </FormControl>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export { UpdateCandidateNotes };
